'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { type JSONContent } from '@tiptap/core';
import { BookOpen, ChevronRight, Copy, Star, Wrench } from 'lucide-react';
import { Tooltip } from './Tooltip';
import { TiptapEditor } from './TiptapEditor';
import { CollapsibleContent } from './CollapsibleContent';
import { MetaBadge } from './MetaBadge';
import type {
  DaemonTrigger,
  DaemonTriggerInjectedConvention,
  DaemonTriggerLog,
  MergedRunnerModelsByType,
  RunnerModelsByType,
  RunnerType,
} from '../lib/api';
import type { RunnerRequestDetailAdapter } from '../../adapter';
import { runnerTypeValues } from '../lib/api';
import { getEffortLevelLabel, getSupportedEffortLevels } from '../lib/runnerEffort';
import { type ActiveConcurrencyWarningReason } from '../lib/daemonTriggerConflict';
import { activeConcurrencyWarningMessageKey } from '../lib/daemonTriggerConflictMessages';
import { copyTextToClipboard } from '../lib/clipboard';
import { Select, type SelectOption } from './Select';
import { EffortSlider } from './EffortSlider';
import { PromptPresetModal } from './PromptPresetModal';
import {
  daemonTriggerStatusBadge,
  daemonTriggerStatusLabel,
  daemonTriggerLogLevelBadge,
  worktreeStatusBadge,
  conventionCategoryBadge,
} from '../lib/badgeColors';
import { UserConventionSelector, type UserConventionOption } from './UserConventionSelector';
import {
  formatDaemonTriggerDate,
  formatHeartbeatRelative,
  formatDuration,
  isTerminalStatus,
  hasContent,
  getTextLength,
  getRunnerTypeLabel,
  getRunnerTypeIcon,
  appendPromptContent,
  buildDaemonTriggerContinueOverrides,
  DEFAULT_LOG_VISIBILITY_MODE,
  filterLogsByMode,
  formatCodexReadableLogs,
  getLogMessagePreview,
  getToolLogPresentation,
  groupAdjacentLogs,
  normalizeLegacyLogCategories,
  sanitizeLegacyCodeReviewPrompt,
  type LogVisibilityMode,
} from './daemonTriggerDetailUtils';
import { formatCompactTokenCount } from '../lib/formatNumber';
import { formatLocalDate, formatLocalTime } from '../lib/dateTime';
import { extractQuestionsForUserMarkdown, getQuestionsForUserMarkdown } from '../hooks/useDaemonTriggerModal';
import QuestionsForUserPanel from './QuestionsForUserPanel';
import { cn } from '../lib/cn';
import { AttachmentPendingChips } from './AttachmentPendingChips';
import { AttachmentAddButton } from './AttachmentAddButton';
import { AttachmentDropZone } from './AttachmentDropZone';
import type { PendingAttachmentDraft } from '../types/attachment';
import { WorktreeAliasEditor } from './WorktreeAliasEditor';
import type { DaemonTriggerRunnerBoxInfo } from '../lib/daemonTriggerRunnerBox';
import { DaemonTriggerTokenUsage } from './DaemonTriggerTokenUsage';

const LOG_VISIBILITY_MODES: LogVisibilityMode[] = ['RESULT_ONLY', 'WITH_TOOL', 'ALL'];

export type DaemonTriggerDetailProps = {
  trigger: DaemonTrigger | null;
  isOpen: boolean;
  onClose: () => void;
  showCoActionLink?: boolean;
  isLoading?: boolean;
  agentName?: string;
  agentOsType?: string | null;
  daemonName?: string;
  daemonOsType?: string | null;
  isDeleting?: boolean;
  onDeleteAction?: (trigger: DaemonTrigger) => void;
  onCancelAction?: () => Promise<DaemonTrigger>;
  isCancelPending?: boolean;
  onContinueAction?: (
    prompt: object,
    overrides?: {
      runnerType?: string;
      model?: string | null;
      fastMode?: boolean;
      planMode?: boolean;
      userConventionIds?: string[];
      attachmentDrafts?: Array<{ key: string; originalName: string }>;
    },
  ) => Promise<DaemonTrigger>;
  isContinuePending?: boolean;
  isContinuePlanModeForced?: boolean;
  /**
   * 이어쓰기 엔진 선택지에 노출할 러너 엔진. 호스트가 트리거의 러너 기준으로 계산해 넘긴다.
   * 미지정(null·undefined)이면 전체 엔진을 보여준다.
   */
  supportedContinueRunnerTypes?: RunnerType[] | null;
  chainLength?: number;
  chainTokenCount?: number | null;
  onNewRequestWithWorktreeAction?: (
    prompt: object,
    options?: { attachmentDrafts?: Array<{ key: string; originalName: string }> },
  ) => Promise<DaemonTrigger>;
  isNewRequestPending?: boolean;
  activeConcurrencyWarningReason?: ActiveConcurrencyWarningReason | null;
  onWorktreeManageAction?: () => void;
  runnerBoxInfo?: DaemonTriggerRunnerBoxInfo | null;
  userConventionOptions?: UserConventionOption[];
  presetContext?: {
    token: string | null;
    projectId: string;
    memberId: string | null;
    /** 프리셋 쓰기 가능 여부. 미지정 시 기존 동작(쓰기 UI 표시)을 유지한다. */
    canWrite?: boolean;
  };
  onReviewAction?: (decision: 'approve' | 'reject') => Promise<void>;
  isReviewPending?: boolean;
  /**
   * 처리 중 상태를 부모에 알림. 부모(DaemonTriggerDetailPanel)가 ResizableDetailPanel의 canClose에
   * 전달해 backdrop/ESC 닫기를 차단한다.
   */
  onBusyChange?: (busy: boolean) => void;
  /**
   * 글로벌 continue 모달이 같은 trigger를 source로 가지고 있는 경우의 ID. 사용자가 "원본 상세 보기"
   * 경로로 들어온 상태를 감지해, 상단에 복귀 배너를 노출하고 자체 이어쓰기 시작 버튼을 "돌아가기"로
   * 전환해 작성 중인 draft를 잃지 않도록 한다.
   */
  activeContinueDraftTriggerId?: string | null;
};

export type DaemonTriggerDetailSlots = {
  /** 마크다운 본문 렌더러. 호스트가 공급한다(공용 패키지는 마크다운 뷰어를 소유하지 않는다). */
  renderMarkdown: (content: string) => ReactNode;
  /** 요청 프롬프트 읽기 전용 뷰어. 호스트가 공급한다(tiptap 뷰어·머메이드 체인은 앱에 남는다). */
  renderPromptViewer: (content: string) => ReactNode;
  /** 첨부 개수·브라우저 진입점. 호스트가 공급한다(첨부 조회·브라우저는 앱에 남는다). */
  renderAttachmentAction: (triggerId: string) => ReactNode;
  /** 프롬프트 프리셋 저장 버튼. 호스트가 공급한다(프리셋 생성 뮤테이션은 앱에 남는다). */
  renderSavePresetButton: (input: {
    token: string | null;
    projectId: string;
    prompt: object;
    userConventionIds: string[];
    userConventionOptions: UserConventionOption[];
    canWrite: boolean;
  }) => ReactNode;
};

export type SharedDaemonTriggerDetailProps = DaemonTriggerDetailProps & {
  detail: RunnerRequestDetailAdapter<
    DaemonTriggerLog,
    DaemonTriggerInjectedConvention,
    RunnerModelsByType,
    MergedRunnerModelsByType
  >;
} & DaemonTriggerDetailSlots;

type ContentProps = SharedDaemonTriggerDetailProps;
const detailHeaderIconButtonClassName = 'btn btn-ghost btn-sm btn-circle h-8 min-h-8 w-8 p-0';
const detailHeaderIconClassName = 'h-5 w-5';
const detailHeaderFavoriteIconClassName = 'h-4 w-4';

const OsIcon = ({ osType }: { osType: string | null | undefined }) => {
  if (osType === 'MACOS')
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M16.2 12.5c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-2-1.5-.2-2.8.9-3.6.9s-2-.9-3.2-.9c-1.7 0-3.2 1-4.1 2.5-1.8 3.1-.4 7.8 1.3 10.2.8 1.2 1.8 2.5 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.8 1.3-2.9-.1 0-2.7-1.1-2.8-4.2Z" />
        <path d="M13.8 5.3c.7-.9 1.2-2.1 1-3.3-1 .1-2.2.7-2.9 1.6-.6.8-1.2 2-1 3.2 1.1.1 2.2-.6 2.9-1.5Z" />
      </svg>
    );
  if (osType === 'WINDOWS')
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M3 5.2 10.7 4v7H3V5.2Zm8.8-1.3L21 2.5V11h-9.2V3.9ZM3 12.9h7.7v7L3 18.8v-5.9Zm8.8 0H21v8.5l-9.2-1.3v-7.2Z" />
      </svg>
    );
  if (osType === 'LINUX')
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 2.5c-2.2 0-4 2.3-4 5.1 0 1.4.4 2.8 1.1 3.7-.6.6-1.1 1.6-1.1 2.9 0 .7.2 1.3.4 1.9l-1.9 1.2c-.7.4-1 1.2-.7 1.9l.3.8c.2.6.9 1 1.6.8l2.6-.7.6 1.3c.2.5.8.8 1.3.8s1-.3 1.3-.8l.6-1.3 2.6.7c.7.2 1.4-.2 1.6-.8l.3-.8c.3-.7 0-1.5-.7-1.9l-1.9-1.2c.3-.6.4-1.2.4-1.9 0-1.3-.5-2.3-1.1-2.9.7-.9 1.1-2.3 1.1-3.7 0-2.8-1.8-5.1-4-5.1Zm-1.6 5.1c.4 0 .7.4.7.8s-.3.8-.7.8-.7-.4-.7-.8.3-.8.7-.8Zm3.2 0c.4 0 .7.4.7.8s-.3.8-.7.8-.7-.4-.7-.8.3-.8.7-.8Zm-3.1 3.4h3c-.1.8-.7 1.4-1.5 1.4s-1.4-.6-1.5-1.4Z" />
      </svg>
    );
  return null;
};

function DaemonTriggerDetailSkeleton({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="shrink-0 px-4 py-4 sm:px-6">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="skeleton h-4 w-24 rounded" />
            <span aria-hidden className="text-base-content/20">
              ·
            </span>
            <div className="skeleton h-4 w-32 rounded" />
          </div>
          <div className="flex items-center gap-2">
            <div className="skeleton h-3 w-12 rounded" />
            <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={onClose} aria-label="close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="shrink-0 px-4 sm:px-6">
        <div className="w-full border-b border-base-300" />
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden p-4 sm:p-6">
        <div className="flex h-full w-full min-w-0 flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="skeleton h-6 w-20 rounded-full" />
            <span aria-hidden className="text-base-content/20">
              ·
            </span>
            <div className="skeleton h-4 w-24 rounded" />
          </div>
          <div>
            <div className="skeleton mb-3 h-3 w-16 rounded" />
            <div className="space-y-2">
              <div className="skeleton h-4 w-full rounded" />
              <div className="skeleton h-4 w-5/6 rounded" />
              <div className="skeleton h-4 w-3/4 rounded" />
            </div>
          </div>
          <div className="-mt-2 flex min-h-0 flex-1 flex-col">
            <div className="mb-3 flex items-center gap-3">
              <div className="skeleton h-4 w-16 rounded" />
              <div className="skeleton h-4 w-16 rounded" />
            </div>
            <div className="min-h-0 flex-1 space-y-2 rounded-xl border border-base-300 bg-base-200 p-3">
              <div className="skeleton h-3 w-full rounded" />
              <div className="skeleton h-3 w-11/12 rounded" />
              <div className="skeleton h-3 w-3/4 rounded" />
              <div className="skeleton h-3 w-full rounded" />
              <div className="skeleton h-3 w-5/6 rounded" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function DaemonTriggerDetailContent({
  trigger,
  isOpen,
  onClose,
  showCoActionLink = true,
  isLoading = false,
  agentName,
  agentOsType,
  daemonName,
  daemonOsType,
  isDeleting = false,
  onDeleteAction,
  onCancelAction,
  isCancelPending = false,
  onContinueAction,
  isContinuePending = false,
  isContinuePlanModeForced = false,
  supportedContinueRunnerTypes = null,
  chainTokenCount,
  onNewRequestWithWorktreeAction,
  isNewRequestPending = false,
  activeConcurrencyWarningReason = null,
  onWorktreeManageAction,
  runnerBoxInfo,
  userConventionOptions,
  presetContext,
  onReviewAction,
  isReviewPending = false,
  onBusyChange,
  activeContinueDraftTriggerId = null,
  detail,
  renderMarkdown,
  renderPromptViewer,
  renderAttachmentAction,
  renderSavePresetButton,
}: ContentProps) {
  const t = useTranslations('daemonTriggers.detail');
  const tCommon = useTranslations('common');
  const tPage = useTranslations('daemonTriggers.page');
  const tLayout = useTranslations('components.appLayout');
  const tWorktreeAlias = useTranslations('components.worktreeAliasEditor');
  const tRoot = useTranslations();
  const formatRelativeTime = detail.formatRelativeTime;
  const [isLogsFullscreenOpen, setIsLogsFullscreenOpen] = useState(false);
  const [logVisibilityMode, setLogVisibilityMode] = useState<LogVisibilityMode>(DEFAULT_LOG_VISIBILITY_MODE);
  const [expandedLogGroupIds, setExpandedLogGroupIds] = useState<Set<string>>(() => new Set());
  const [expandedLogMessageIds, setExpandedLogMessageIds] = useState<Set<string>>(() => new Set());
  const [isRetryConfirmOpen, setIsRetryConfirmOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isChainWarningOpen, setIsChainWarningOpen] = useState(false);
  const [continueAsNewRequest, setContinueAsNewRequest] = useState(false);
  const [isContinueInputOpen, setIsContinueInputOpen] = useState(false);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [isInjectedConventionsOpen, setIsInjectedConventionsOpen] = useState(false);
  const [continuePrompt, setContinuePrompt] = useState<JSONContent | undefined>(undefined);
  const [continueAttachmentDrafts, setContinueAttachmentDrafts] = useState<PendingAttachmentDraft[]>([]);
  const [isContinueSubmitting, setIsContinueSubmitting] = useState(false);
  const [isCopyingRunnerPrompt, setIsCopyingRunnerPrompt] = useState(false);
  const isContinueSubmittingRef = useRef(false);
  useEffect(() => {
    onBusyChange?.(isContinueSubmitting);
  }, [isContinueSubmitting, onBusyChange]);
  const handleSafeClose = useCallback(() => {
    if (isContinueSubmittingRef.current) {
      detail.feedback.warning(tLayout('submittingWaitNotice'));
      return;
    }
    onClose();
  }, [onClose, tLayout]);
  const [continueRunnerType, setContinueRunnerType] = useState('');
  const [continueModel, setContinueModel] = useState('');
  const [continueFastMode, setContinueFastMode] = useState(trigger?.fastMode ?? false);
  // Effort는 fastMode와 별도 상태로 유지한다. '' = 모델 기본값.
  const [continueEffort, setContinueEffort] = useState(trigger?.effort ?? '');
  const [continuePlanMode, setContinuePlanMode] = useState(trigger?.planMode ?? false);
  const [continueUserConventionIds, setContinueUserConventionIds] = useState<string[]>([]);
  const triggerId = trigger?.id;
  const triggerRunnerType = trigger?.runnerType ?? '';
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;
  const effectiveContinuePlanMode = isContinuePlanModeForced || continuePlanMode;
  const PROMPT_MAX_LENGTH = 1000;
  const continuePromptLength = useMemo(() => getTextLength(continuePrompt), [continuePrompt]);
  const isContinuePromptOverLimit = continuePromptLength > PROMPT_MAX_LENGTH;
  const [selectedSection, setSelectedSection] = useState<'ERROR' | 'CO_ACTION' | 'LOGS'>('LOGS');
  const token = detail.session.token;
  const memberId = detail.session.memberId;
  const contextQuery = detail.session.scope.contextQuery;
  const toggleFavoriteMutation = detail.useToggleFavoriteMutation(token);
  const worktreeAliasMutation = detail.useWorktreeAliasMutation(token, trigger?.projectId ?? '');
  const isSystemAdmin = detail.session.isSystemAdmin;

  const handleWorktreeAliasSave = useCallback(
    async (worktreeId: string, alias: string) => {
      try {
        await worktreeAliasMutation.mutateAsync({ worktreeId, alias });
        detail.feedback.success(tWorktreeAlias('saved'));
      } catch (error) {
        const message = detail.feedback.resolveError(error, tWorktreeAlias('saveFailed'));
        if (message) detail.feedback.error(message);
        throw error;
      }
    },
    [tRoot, tWorktreeAlias, worktreeAliasMutation],
  );

  // 시스템 관리자 전용(디버깅): 이 트리거에 실제 전송된 러너 프롬프트를 서버에서 재구성해 복사한다.
  const handleCopyRunnerPrompt = useCallback(async () => {
    if (!token || !trigger?.id) {
      return;
    }
    setIsCopyingRunnerPrompt(true);
    try {
      const { runnerPrompt } = await detail.fetchPromptPreview(token, trigger.id);
      await copyTextToClipboard(runnerPrompt);
      detail.feedback.success(tLayout('daemonTriggerPromptCopied'));
    } catch (error) {
      const message = detail.feedback.resolveError(error, tLayout('daemonTriggerCopyPromptFailed'));
      if (message) detail.feedback.error(message);
    } finally {
      setIsCopyingRunnerPrompt(false);
    }
  }, [token, trigger?.id, tRoot, tLayout]);
  const { data: publicRunnerModels, isPending: isPublicRunnerModelsPending } = detail.usePublicRunnerModels();
  const { data: mergedRunnerModels, isPending: isMergedRunnerModelsPending } = detail.useMergedRunnerModels(
    token,
    memberId,
  );
  const runnerModels = mergedRunnerModels ?? publicRunnerModels;
  // 데이터 유무가 아니라 쿼리 상태로 판단한다. 조회가 오류로 끝나도 data는 undefined로 남으므로,
  // 데이터 유무로 보면 영영 "로딩 중"이 되어 stale effort 정리 효과가 다시 실행되지 않는다.
  const areRunnerModelsLoading = token && memberId ? isMergedRunnerModelsPending : isPublicRunnerModelsPending;

  const effectiveRunnerType = (continueRunnerType || triggerRunnerType || '') as RunnerType;

  // 이어쓰기는 같은 러너에서 실행되므로, 호스트가 넘긴 엔진만 보여준다(요청 모달과 같은 규칙).
  // 원래 요청의 엔진은 러너 설정이 바뀌었더라도 현재 값으로 보이도록 남겨 둔다.
  const continueRunnerTypeOptions = useMemo(() => {
    const visible = supportedContinueRunnerTypes
      ? runnerTypeValues.filter((rt) => supportedContinueRunnerTypes.includes(rt) || rt === triggerRunnerType)
      : runnerTypeValues;
    return visible.map((rt) => ({
      value: rt,
      label: getRunnerTypeLabel(rt, tLayout),
    }));
  }, [tLayout, supportedContinueRunnerTypes, triggerRunnerType]);

  const toFastModeModelOptionValue = (modelValue: string) => `__fast__:${modelValue}`;
  const decodeContinueModelOptionValue = (optionValue: string): { model: string; fastMode: boolean } => {
    if (optionValue.startsWith('__fast__:')) {
      return {
        model: optionValue.slice('__fast__:'.length),
        fastMode: true,
      };
    }

    return {
      model: optionValue,
      fastMode: false,
    };
  };

  const isContinueFastModeModelSupported = useMemo(() => {
    const models = runnerModels?.[effectiveRunnerType] ?? [];
    return continueModel.length > 0 && models.some((model) => model.value === continueModel && model.fastModeSupported);
  }, [continueModel, effectiveRunnerType, runnerModels]);

  const continueModelOptionValue = useMemo(
    () =>
      continueFastMode && isContinueFastModeModelSupported ? toFastModeModelOptionValue(continueModel) : continueModel,
    [continueFastMode, continueModel, isContinueFastModeModelSupported],
  );

  const continueModelOptions = useMemo<SelectOption[]>(() => {
    const models = runnerModels?.[effectiveRunnerType] ?? [];
    return [
      { value: '', label: t('continueModelDefault') },
      ...models.flatMap((model) => {
        const label = model.label;
        const options: SelectOption[] = [
          {
            value: model.value,
            label,
          },
        ];

        if (model.fastModeSupported) {
          options.push({
            value: toFastModeModelOptionValue(model.value),
            label: `⚡ ${label}`,
          });
        }

        return options;
      }),
    ];
  }, [effectiveRunnerType, runnerModels, t]);

  // 명시적으로 선택된 continue 모델의 지원 Effort 레벨만 노출한다.
  const availableContinueEffortLevels = useMemo<string[]>(() => {
    if (continueModel.length === 0) {
      return [];
    }
    const models = runnerModels?.[effectiveRunnerType] ?? [];
    return getSupportedEffortLevels(
      effectiveRunnerType,
      models.find((model) => model.value === continueModel)?.supportedEffortLevels,
    );
  }, [continueModel, effectiveRunnerType, runnerModels]);
  const showContinueEffortSelect = availableContinueEffortLevels.length > 0;
  const continueEffortOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: tLayout('daemonEffortModelDefault') },
      ...availableContinueEffortLevels.map((level) => ({ value: level, label: getEffortLevelLabel(level, tLayout) })),
    ],
    [availableContinueEffortLevels, tLayout],
  );

  useEffect(() => {
    const currentTrigger = triggerRef.current;
    if (currentTrigger) {
      setContinueRunnerType(currentTrigger.runnerType ?? '');
      setContinueModel(currentTrigger.model ?? '');
      setContinueFastMode(currentTrigger.fastMode ?? false);
      setContinueEffort(currentTrigger.effort ?? '');
      setContinueUserConventionIds(currentTrigger.userConventionIds ?? []);
      setIsInjectedConventionsOpen(false);
    }
  }, [triggerId]);

  useEffect(() => {
    if (continueRunnerType && continueRunnerType !== triggerRunnerType) {
      setContinueModel('');
      setContinueFastMode(false);
      setContinueEffort('');
    }
  }, [continueRunnerType, triggerRunnerType]);

  // 러너/모델 변경으로 현재 Effort가 새 모델의 지원 레벨에 없으면 즉시 초기화한다(stale 값 전송 방지).
  useEffect(() => {
    // 목록 로딩 중에는 빈 지원 정보로 판단해 원본 상속값을 지우지 않는다.
    if (areRunnerModelsLoading) return;
    if (continueEffort.length > 0 && !availableContinueEffortLevels.includes(continueEffort)) {
      setContinueEffort('');
    }
  }, [areRunnerModelsLoading, availableContinueEffortLevels, continueEffort]);

  // 진행 중(아직 로그를 더 만들 수 있는) 트리거만 폴링한다. NEEDS_REVIEW는 일시정지
  // 상태로 이 트리거에 새 로그가 추가되지 않으므로 폴링 대상에서 제외한다.
  const isLogStreamActive = trigger ? !isTerminalStatus(trigger.status) && trigger.status !== 'NEEDS_REVIEW' : false;
  const { logs } = detail.useTriggerLogs(trigger?.id ?? null, { active: isLogStreamActive });
  const displayLogs = useMemo(
    () => normalizeLegacyLogCategories(trigger?.runnerType === 'CODEX' ? formatCodexReadableLogs(logs) : logs),
    [logs, trigger?.runnerType],
  );
  const visibleLogs = useMemo(() => filterLogsByMode(displayLogs, logVisibilityMode), [displayLogs, logVisibilityMode]);
  const groupedDisplayLogs = useMemo(() => groupAdjacentLogs(visibleLogs), [visibleLogs]);

  const { data: injectedConventions = [] } = detail.useInjectedConventions(trigger?.id ?? null);

  const logsContainerRef = useRef<HTMLDivElement>(null);
  const logsFullscreenRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const isFullscreenAtBottomRef = useRef(true);

  const handleLogsScroll = useCallback(() => {
    const el = logsContainerRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
  }, []);

  const handleFullscreenLogsScroll = useCallback(() => {
    const el = logsFullscreenRef.current;
    if (!el) return;
    isFullscreenAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
  }, []);

  useEffect(() => {
    const el = logsContainerRef.current;
    if (el && selectedSection === 'LOGS' && isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    const fullEl = logsFullscreenRef.current;
    if (fullEl && isFullscreenAtBottomRef.current) {
      fullEl.scrollTop = fullEl.scrollHeight;
    }
  }, [displayLogs, selectedSection]);

  useEffect(() => {
    isAtBottomRef.current = true;
    isFullscreenAtBottomRef.current = true;
  }, [trigger?.id]);

  useEffect(() => {
    if (!triggerId) return;
    setSelectedSection(
      (trigger.worktreeError ?? trigger.errorMessage)
        ? 'ERROR'
        : trigger.status === 'DONE' && trigger.linkedCoAction
          ? 'CO_ACTION'
          : 'LOGS',
    );
  }, [trigger?.errorMessage, trigger?.linkedCoAction, trigger?.status, trigger?.worktreeError, triggerId]);

  useEffect(() => {
    setIsLogsFullscreenOpen(false);
    setIsContinueInputOpen(false);
    setIsRetryConfirmOpen(false);
    setIsCancelConfirmOpen(false);
    setIsChainWarningOpen(false);
    setContinueAsNewRequest(false);
    setContinuePrompt(undefined);
    setContinueAttachmentDrafts([]);
    setContinuePlanMode(triggerRef.current?.planMode ?? false);
  }, [triggerId]);

  const questionsForUser = useMemo(
    () => extractQuestionsForUserMarkdown(getQuestionsForUserMarkdown(trigger)),
    [trigger],
  );
  const displayPrompt = useMemo(
    () => sanitizeLegacyCodeReviewPrompt(trigger?.prompt ?? {}, trigger?.runnerDispatchPolicyId),
    [trigger?.prompt, trigger?.runnerDispatchPolicyId],
  );

  const DEFAULT_MAX_INPUT_TOKENS = 200_000;
  const TOKEN_WARNING_RATIO = 0.5;
  const currentModel = useMemo(() => {
    const rt = (trigger?.runnerType ?? '') as RunnerType;
    const modelValue = trigger?.model ?? '';
    const models = runnerModels?.[rt] ?? [];
    return models.find((m) => m.value === modelValue);
  }, [trigger?.runnerType, trigger?.model, runnerModels]);

  if (!isOpen) return null;
  if (!trigger) return isLoading ? <DaemonTriggerDetailSkeleton onClose={onClose} /> : null;

  const modelLabel =
    trigger.model && runnerModels
      ? (runnerModels[trigger.runnerType as keyof typeof runnerModels]?.find((m) => m.value === trigger.model)?.label ??
        trigger.model)
      : null;
  const runnerModelLabel = modelLabel ?? getRunnerTypeLabel(trigger.runnerType, tLayout);
  const displayedRunnerModelLabel = trigger.fastMode ? `⚡ ${runnerModelLabel}` : runnerModelLabel;
  const coActionContent = trigger.linkedCoActionHistory?.content ?? trigger.linkedCoAction?.content ?? null;
  const displayErrorMessage = trigger.worktreeError ?? trigger.errorMessage;

  const canContinue = isTerminalStatus(trigger.status) && Boolean(onContinueAction);
  const canCancel = trigger.status === 'IN_PROGRESS' && Boolean(onCancelAction);
  const showCoActionSection =
    trigger.status === 'DONE' || trigger.status === 'FAILED' || trigger.status === 'CANCELLED';
  const isErrorSelected = selectedSection === 'ERROR';
  const isCoActionSelected = selectedSection === 'CO_ACTION';
  const continueSelectedModel =
    runnerModels?.[effectiveRunnerType]?.find((model) => model.value === continueModel) ?? currentModel ?? null;
  const continueMaxInputTokens = continueSelectedModel?.maxInputTokens ?? DEFAULT_MAX_INPUT_TOKENS;
  const continueHistoryPercent = Math.round(((chainTokenCount ?? 0) / continueMaxInputTokens) * 100);
  const tokenThreshold = continueMaxInputTokens * TOKEN_WARNING_RATIO;
  const showChainWarning = (chainTokenCount ?? 0) >= tokenThreshold;
  const canNewRequestWithWorktree = trigger.useWorktree && !!onNewRequestWithWorktreeAction;
  // "원본 상세 보기" 경로로 이 detail에 들어왔는지 — 글로벌 continue 모달이 같은 trigger를 draft 중일 때 true.
  const hasActiveContinueDraft = activeContinueDraftTriggerId === trigger.id;

  const handleContinue = () => {
    if (trigger.status === 'FAILED') {
      setIsRetryConfirmOpen(true);
      return;
    }
    if (showChainWarning) {
      setIsChainWarningOpen(true);
      return;
    }
    setContinueAsNewRequest(false);
    setIsContinueInputOpen(true);
  };

  const handleChainWarningContinueAnyway = () => {
    setIsChainWarningOpen(false);
    setContinueAsNewRequest(false);
    setIsContinueInputOpen(true);
  };

  const handleChainWarningNewRequest = () => {
    setIsChainWarningOpen(false);
    setContinueAsNewRequest(true);
    setIsContinueInputOpen(true);
  };

  const handleContinueCancel = () => {
    setIsContinueInputOpen(false);
    setContinueAsNewRequest(false);
    setContinuePrompt(undefined);
  };

  const handleCancel = async () => {
    if (!onCancelAction) return;
    try {
      await onCancelAction();
      setIsCancelConfirmOpen(false);
    } catch {
      // error handled by caller
    }
  };

  const handleToggleFavorite = () => {
    if (!trigger?.id || toggleFavoriteMutation.isPending) return;
    toggleFavoriteMutation
      .mutateAsync({ triggerId: trigger.id, isFavorite: !trigger.isFavorite })
      .then(
        (result) => {
          const isFavorite = (result as { isFavorite?: boolean } | null | undefined)?.isFavorite ?? false;
          detail.feedback.success(isFavorite ? tPage('favoriteAdded') : tPage('favoriteRemoved'));
        },
        () => {
          detail.feedback.error(tPage('favoriteFailed'));
        },
      )
      .catch(() => {
        // error handled by caller
      });
  };

  const buildOverrides = () => {
    return buildDaemonTriggerContinueOverrides({
      source: trigger,
      continueRunnerType,
      continueModel,
      continueFastMode,
      continueEffort,
      effectiveContinuePlanMode,
      continueUserConventionIds,
    });
  };

  const handleContinueModelChange = (value: string) => {
    const decoded = decodeContinueModelOptionValue(value);
    if (decoded.model !== continueModel) {
      setContinueEffort('');
    }
    setContinueModel(decoded.model);
    setContinueFastMode(decoded.fastMode);
  };

  const handleContinueRunnerTypeChange = (value: string) => {
    if (value !== continueRunnerType) {
      setContinueEffort('');
    }
    setContinueRunnerType(value);
  };

  const toggleContinueUserConventionId = (conventionId: string, checked: boolean) => {
    setContinueUserConventionIds((prev) => {
      if (checked) {
        if (prev.includes(conventionId)) return prev;
        return [...prev, conventionId];
      }
      return prev.filter((id) => id !== conventionId);
    });
  };

  const handleContinueSubmit = async () => {
    if (!continuePrompt || !hasContent(continuePrompt)) return;
    if (isContinueSubmittingRef.current) return;
    isContinueSubmittingRef.current = true;
    setIsContinueSubmitting(true);
    try {
      const attachmentDraftsPayload =
        continueAttachmentDrafts.length > 0
          ? continueAttachmentDrafts.map((draft) => ({ key: draft.key, originalName: draft.originalName }))
          : undefined;

      if (continueAsNewRequest && onNewRequestWithWorktreeAction) {
        await onNewRequestWithWorktreeAction(
          continuePrompt as object,
          attachmentDraftsPayload ? { attachmentDrafts: attachmentDraftsPayload } : undefined,
        );
      } else if (onContinueAction) {
        const overrides = buildOverrides();
        await onContinueAction(continuePrompt as object, {
          ...overrides,
          ...(attachmentDraftsPayload ? { attachmentDrafts: attachmentDraftsPayload } : {}),
        });
      } else {
        return;
      }
      setIsContinueInputOpen(false);
      setContinueAsNewRequest(false);
      setContinuePrompt(undefined);
      setContinueAttachmentDrafts([]);
    } catch {
      // 에러 처리는 호출부(page)에서 담당
    } finally {
      isContinueSubmittingRef.current = false;
      setIsContinueSubmitting(false);
    }
  };

  const handleRetryConfirm = async () => {
    if (!onContinueAction) return;
    try {
      // 같은 프롬프트로 재시도하되, 사용자가 고른 엔진/모델/패스트모드 오버라이드를 전달한다.
      // 원본이 아니라 화면에 보이는 정제본을 복제한다 — 숨긴 레거시 메타데이터가 새 요청에 다시 실리면
      // 사용자가 무엇을 보냈는지 화면으로 확인할 수 없다.
      await onContinueAction(displayPrompt as object, buildOverrides());
      setIsRetryConfirmOpen(false);
    } catch {
      // 에러 처리는 호출부(page)에서 담당
    }
  };

  const toggleLogGroup = (groupId: string) => {
    setExpandedLogGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleLogMessage = (logId: string) => {
    setExpandedLogMessageIds((current) => {
      const next = new Set(current);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const renderLog = (log: DaemonTriggerLog) => (
    <div key={log.id} className="grid min-w-0 grid-cols-[3.25rem_minmax(0,1fr)] gap-3 py-1.5 text-xs leading-relaxed">
      <time className="font-mono text-[11px] text-[var(--color-fg-subtle)]" dateTime={log.createdAt}>
        {formatLocalTime(log.createdAt)}
      </time>
      <div className="min-w-0 border-l border-base-300 pl-3">
        {(() => {
          const isExpanded = expandedLogMessageIds.has(log.id);
          const levelBadge =
            log.level === 'INFO' ? null : (
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                  daemonTriggerLogLevelBadge[log.level],
                )}
              >
                {log.level}
              </span>
            );

          if (log.category === 'TOOL') {
            const presentation = getToolLogPresentation(log);
            const targetPreview = presentation.target
              ? getLogMessagePreview(presentation.target)
              : { text: '', isTruncated: false };

            return (
              <div className="min-w-0 text-base-content/80">
                <div className="flex min-w-0 items-center gap-2">
                  <Wrench className="h-3.5 w-3.5 shrink-0 text-[var(--color-fg-subtle)]" aria-hidden="true" />
                  <span className="shrink-0 font-mono text-xs font-semibold">
                    {presentation.toolName ?? t('logUnknownTool')}
                  </span>
                  {levelBadge}
                </div>
                {presentation.target ? (
                  <div className="mt-1 min-w-0">
                    <code className="whitespace-pre-wrap break-all font-mono text-[11px] text-[var(--color-fg-subtle)]">
                      {isExpanded ? presentation.target : targetPreview.text}
                    </code>
                    {targetPreview.isTruncated ? (
                      <button
                        type="button"
                        className="ml-2 text-[11px] font-medium text-primary hover:underline"
                        aria-expanded={isExpanded}
                        onClick={() => toggleLogMessage(log.id)}
                      >
                        {isExpanded ? t('logMessageShowLess') : t('logMessageShowMore')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          }

          const messagePreview = getLogMessagePreview(log.message);
          return (
            <div className="min-w-0">
              {levelBadge ? <div className="mb-1 flex items-center gap-2">{levelBadge}</div> : null}
              <pre className="whitespace-pre-wrap break-words font-sans text-xs text-base-content/80">
                {isExpanded ? log.message : messagePreview.text}
              </pre>
              {messagePreview.isTruncated ? (
                <button
                  type="button"
                  className="mt-1 text-[11px] font-medium text-primary hover:underline"
                  aria-expanded={isExpanded}
                  onClick={() => toggleLogMessage(log.id)}
                >
                  {isExpanded ? t('logMessageShowLess') : t('logMessageShowMore')}
                </button>
              ) : null}
            </div>
          );
        })()}
      </div>
    </div>
  );

  const logsContent = (
    <>
      {selectedSection === 'ERROR' ? (
        <p className="text-sm text-error whitespace-pre-wrap break-words">{displayErrorMessage}</p>
      ) : selectedSection === 'CO_ACTION' ? (
        coActionContent ? (
          <div className="space-y-3 overflow-x-hidden">
            <div className="[&_.prose]:!text-sm [&_.prose]:!max-w-none [&_.prose]:overflow-x-auto [&_.prose_h1]:!text-base [&_.prose_h2]:!text-base [&_.prose_h3]:!text-sm [&_.prose_li]:!text-sm [&_.prose_p]:!text-sm [&_.prose_pre]:overflow-x-auto [&_.prose_code]:break-all [&_.prose_table]:block [&_.prose_table]:overflow-x-auto">
              {renderMarkdown(coActionContent)}
            </div>
          </div>
        ) : (
          <p className="text-xs text-[var(--color-fg-muted)]">No CoAction yet.</p>
        )
      ) : (
        <>
          <div className="sticky top-0 z-10 mb-1 bg-base-100/95 px-1 pb-2 backdrop-blur-sm">
            <div
              role="group"
              aria-label={t('logVisibilityLabel')}
              className="inline-flex flex-wrap items-center rounded-lg bg-base-200 p-0.5"
            >
              {LOG_VISIBILITY_MODES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={logVisibilityMode === mode}
                  className={cn(
                    'btn btn-xs h-7 min-h-7 rounded-md border-0 px-2.5',
                    logVisibilityMode === mode
                      ? 'bg-base-100 text-base-content shadow-sm'
                      : 'btn-ghost text-[var(--color-fg-muted)]',
                  )}
                  onClick={() => setLogVisibilityMode(mode)}
                >
                  {t(`logVisibility_${mode}`)}
                </button>
              ))}
            </div>
          </div>
          {visibleLogs.length === 0 ? (
            <p className="text-xs text-[var(--color-fg-muted)]">{t('logsEmpty')}</p>
          ) : (
            (() => {
              let previousDate = '';
              return groupedDisplayLogs.map((entry) => {
                const firstLog = entry.kind === 'single' ? entry.log : entry.items[0];
                if (!firstLog) return null;
                const currentDate = formatLocalDate(firstLog.createdAt);
                const showDateSeparator = currentDate !== previousDate;
                previousDate = currentDate;

                if (entry.kind === 'single') {
                  return (
                    <div key={entry.log.id}>
                      {showDateSeparator ? (
                        <div className="mb-1 border-b border-base-300 py-1.5 text-[11px] font-medium text-[var(--color-fg-subtle)]">
                          {currentDate}
                        </div>
                      ) : null}
                      {renderLog(entry.log)}
                    </div>
                  );
                }

                const groupId = firstLog.id;
                const isExpanded = expandedLogGroupIds.has(groupId);

                return (
                  <div key={groupId}>
                    {showDateSeparator ? (
                      <div className="mb-1 border-b border-base-300 py-1.5 text-[11px] font-medium text-[var(--color-fg-subtle)]">
                        {currentDate}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="grid w-full min-w-0 grid-cols-[3.25rem_minmax(0,1fr)] gap-3 py-1.5 text-left text-[var(--color-fg-muted)] hover:text-base-content"
                      aria-expanded={isExpanded}
                      title={isExpanded ? tCommon('actions.collapse') : tCommon('actions.expand')}
                      onClick={() => toggleLogGroup(groupId)}
                    >
                      <time
                        className="font-mono text-[11px] text-[var(--color-fg-subtle)]"
                        dateTime={firstLog.createdAt}
                      >
                        {formatLocalTime(firstLog.createdAt)}
                      </time>
                      <span className="flex min-w-0 items-center gap-2 border-l border-base-300 pl-3">
                        <ChevronRight
                          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isExpanded && 'rotate-90')}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1 truncate font-mono text-xs font-medium">
                          {t('logGroupSummary', { toolName: entry.toolName, count: entry.count })}
                        </span>
                        {firstLog.level === 'INFO' ? null : (
                          <span
                            className={cn(
                              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                              daemonTriggerLogLevelBadge[firstLog.level],
                            )}
                          >
                            {firstLog.level}
                          </span>
                        )}
                      </span>
                    </button>
                    {isExpanded ? <div className="pl-3">{entry.items.map(renderLog)}</div> : null}
                  </div>
                );
              });
            })()
          )}
        </>
      )}
    </>
  );

  const sectionTabs = (
    <div role="tablist" className="flex min-w-0 items-end gap-4">
      {displayErrorMessage ? (
        <button
          type="button"
          role="tab"
          aria-selected={selectedSection === 'ERROR'}
          className={cn(
            '-mb-px border-b-2 pb-2 text-sm font-medium transition-colors',
            selectedSection === 'ERROR'
              ? 'border-error text-error'
              : 'border-transparent text-[var(--color-fg-muted)] hover:text-base-content',
          )}
          onClick={() => setSelectedSection('ERROR')}
        >
          Error
        </button>
      ) : null}
      {showCoActionSection ? (
        <button
          type="button"
          role="tab"
          aria-selected={selectedSection === 'CO_ACTION'}
          className={cn(
            '-mb-px border-b-2 pb-2 text-sm font-medium transition-colors',
            selectedSection === 'CO_ACTION'
              ? 'border-primary text-base-content'
              : 'border-transparent text-[var(--color-fg-muted)] hover:text-base-content',
          )}
          onClick={() => setSelectedSection('CO_ACTION')}
        >
          CoAction
        </button>
      ) : null}
      <button
        type="button"
        role="tab"
        aria-selected={selectedSection === 'LOGS'}
        className={cn(
          '-mb-px border-b-2 pb-2 text-sm font-medium transition-colors',
          selectedSection === 'LOGS'
            ? 'border-primary text-base-content'
            : 'border-transparent text-[var(--color-fg-muted)] hover:text-base-content',
        )}
        onClick={() => setSelectedSection('LOGS')}
      >
        {t('labelLogs')}
      </button>
    </div>
  );

  const isInProgress = trigger.status === 'IN_PROGRESS' || trigger.status === 'CANCEL_REQUESTED';

  const sectionActions = (
    <div className="flex shrink-0 items-center gap-1 pb-1.5">
      {isInProgress && trigger.lastHeartbeatAt ? (
        <span className="text-xs text-[var(--color-fg-subtle)] normal-case mr-1">
          {formatHeartbeatRelative(trigger.lastHeartbeatAt, t)}
        </span>
      ) : isTerminalStatus(trigger.status) && trigger.startedAt ? (
        <span className="text-xs text-[var(--color-fg-subtle)] normal-case mr-1">
          {formatDuration(trigger.startedAt, trigger.updatedAt, t)}
        </span>
      ) : null}
      {showCoActionLink && showCoActionSection && trigger.linkedCoAction ? (
        <a
          href={`/co-actions/detail?id=${trigger.linkedCoAction.id}${contextQuery.replace('?', '&')}`}
          className="btn btn-ghost btn-xs btn-circle text-[var(--color-fg-muted)] hover:text-base-content"
          aria-label={t('openSessionSummary')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        </a>
      ) : null}
      <button
        type="button"
        className="btn btn-ghost btn-xs btn-circle text-[var(--color-fg-muted)] hover:text-base-content"
        onClick={() => setIsLogsFullscreenOpen(true)}
        aria-label="Open in fullscreen"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 3H5a2 2 0 00-2 2v3m16 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M8 21H5a2 2 0 01-2-2v-3"
          />
        </svg>
      </button>
    </div>
  );

  const footerContent =
    trigger.status === 'NEEDS_REVIEW' && onReviewAction ? (
      <div className="flex flex-col gap-3">
        <div className="rounded-lg border border-secondary/30 bg-secondary/5 p-3">
          <p className="text-sm font-medium text-base-content mb-1">{t('reviewTitle')}</p>
          <p className="text-xs text-[var(--color-fg-muted)]">{t('reviewDescription')}</p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn btn-outline btn-error btn-sm"
            onClick={() => void onReviewAction('reject')}
            disabled={isReviewPending}
          >
            {t('reviewReject')}
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={() => void onReviewAction('approve')}
            disabled={isReviewPending}
          >
            {isReviewPending ? t('reviewPending') : t('reviewApprove')}
          </button>
        </div>
      </div>
    ) : canContinue ||
      canCancel ||
      trigger.status === 'CANCEL_REQUESTED' ||
      (trigger.status === 'PENDING' && onDeleteAction) ? (
      <>
        {canCancel && isCancelConfirmOpen ? (
          <div className="flex flex-col gap-3">
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3">
              <p className="text-sm font-medium text-base-content mb-1">{t('cancelConfirmTitle')}</p>
              <p className="text-xs text-[var(--color-fg-muted)]">{t('cancelConfirmDescription')}</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setIsCancelConfirmOpen(false)}
                disabled={isCancelPending}
              >
                {tCommon('actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-warning btn-sm"
                onClick={() => void handleCancel()}
                disabled={isCancelPending}
              >
                {isCancelPending ? t('cancelling') : t('cancelConfirm')}
              </button>
            </div>
          </div>
        ) : trigger.status === 'CANCEL_REQUESTED' ? (
          <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs text-[var(--color-fg-muted)]">
            {t('cancelRequestedMessage')}
          </div>
        ) : isChainWarningOpen ? (
          <div className="flex flex-col gap-3">
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3">
              <p className="text-sm font-medium text-base-content mb-1">{t('chainWarningTitle')}</p>
              <p className="text-xs text-[var(--color-fg-muted)]">
                {t('chainWarningDescription', {
                  tokens: formatCompactTokenCount(chainTokenCount),
                  percent: Math.round(
                    ((chainTokenCount ?? 0) / (currentModel?.maxInputTokens ?? DEFAULT_MAX_INPUT_TOKENS)) * 100,
                  ),
                })}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleChainWarningContinueAnyway}>
                {t('chainWarningContinueAnyway')}
              </button>
              {canNewRequestWithWorktree ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={handleChainWarningNewRequest}>
                  {t('chainWarningNewRequest')}
                </button>
              ) : null}
            </div>
          </div>
        ) : isContinueInputOpen ? (
          <div
            className={`flex flex-col gap-3 transition-opacity ${isContinueSubmitting ? 'pointer-events-none opacity-60' : ''}`}
            aria-disabled={isContinueSubmitting}
          >
            <div>
              <p className="text-xs font-semibold text-[var(--color-fg-muted)] mb-1">
                {continueAsNewRequest ? t('chainWarningNewRequest') : tLayout('daemonTriggerContinueRequest')}
              </p>
              <div className="rounded-lg border border-base-300 bg-base-200 p-3 text-xs text-[var(--color-fg-muted)]">
                {continueAsNewRequest
                  ? t('chainWarningDescription', {
                      tokens: formatCompactTokenCount(chainTokenCount),
                      percent: continueHistoryPercent,
                    })
                  : tLayout('daemonTriggerContinueDescription')}
              </div>
            </div>
            {activeConcurrencyWarningReason ? (
              <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs text-warning">
                {tLayout(activeConcurrencyWarningMessageKey(activeConcurrencyWarningReason))}
              </div>
            ) : null}
            {!continueAsNewRequest && chainTokenCount != null && chainTokenCount > 0 ? (
              <div className={`text-xs ${showChainWarning ? 'text-warning' : 'text-[var(--color-fg-subtle)]'}`}>
                {tLayout('historyTokenLabel', {
                  tokens: formatCompactTokenCount(chainTokenCount),
                  maxTokens: formatCompactTokenCount(continueMaxInputTokens),
                  percent: continueHistoryPercent,
                })}
              </div>
            ) : null}
            {!continueAsNewRequest && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex min-w-0 flex-col">
                    <label
                      className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                      htmlFor="daemon-detail-continue-runner-type-select"
                    >
                      {tLayout('runnerType')}
                    </label>
                    <Select
                      id="daemon-detail-continue-runner-type-select"
                      options={continueRunnerTypeOptions}
                      value={continueRunnerType}
                      onChange={handleContinueRunnerTypeChange}
                      size="sm"
                      fullWidth
                    />
                  </div>
                  <div className="relative flex min-w-0 flex-col">
                    <label
                      className="mb-1.5 flex h-4 items-center text-xs font-medium text-[var(--color-fg-subtle)]"
                      htmlFor="daemon-detail-continue-model-select"
                    >
                      {tLayout('runnerModel')}
                    </label>
                    {showContinueEffortSelect ? (
                      <EffortSlider
                        className="absolute right-0 top-0 -translate-y-0.5"
                        id="daemon-detail-continue-effort-select"
                        value={continueEffort}
                        onChange={setContinueEffort}
                        options={continueEffortOptions}
                        title={tLayout('runnerEffort')}
                        autoLabel={tLayout('daemonEffortAuto')}
                        compactAutoLabel={tLayout('daemonEffortDefaultShort')}
                        fasterLabel={tLayout('daemonEffortFaster')}
                        smarterLabel={tLayout('daemonEffortSmarter')}
                      />
                    ) : null}
                    <Select
                      id="daemon-detail-continue-model-select"
                      options={continueModelOptions}
                      value={continueModelOptionValue}
                      onChange={handleContinueModelChange}
                      size="sm"
                      fullWidth
                    />
                  </div>
                </div>
                {userConventionOptions && userConventionOptions.length > 0 ? (
                  <UserConventionSelector
                    label={tLayout('userConventionsLabel')}
                    emptyHint={tLayout('userConventionsEmpty')}
                    conventions={userConventionOptions}
                    selectedIds={continueUserConventionIds}
                    onToggle={toggleContinueUserConventionId}
                    maxHeightClassName="max-h-32"
                  />
                ) : null}
              </div>
            )}
            <QuestionsForUserPanel markdown={questionsForUser} renderMarkdown={renderMarkdown} />
            {/* File drops on the continue prompt are attachment-first; revisit this wrapper before adding editor-level file drop handling. */}
            <AttachmentDropZone
              drafts={continueAttachmentDrafts}
              onChange={setContinueAttachmentDrafts}
              disabled={isContinueSubmitting || isContinuePending || isNewRequestPending}
              className={cn(
                'tiptap-compact-wrapper h-40 overflow-y-auto rounded-lg border transition-colors [&_.ProseMirror]:!px-3 [&_.ProseMirror]:!py-2',
                isContinuePromptOverLimit ? 'border-error/60' : 'border-base-300',
              )}
              activeClassName="border-primary/60 bg-primary/5"
            >
              <TiptapEditor
                value={continuePrompt}
                onChangeAction={setContinuePrompt}
                placeholder={t('continuePromptPlaceholder')}
                hideToolbar
                compact
              />
            </AttachmentDropZone>
            <AttachmentPendingChips
              drafts={continueAttachmentDrafts}
              onChange={setContinueAttachmentDrafts}
              disabled={isContinuePending || isNewRequestPending}
            />
            <div className="flex items-center justify-between">
              {!continueAsNewRequest ? (
                <div className="flex items-center gap-1">
                  <label
                    className={cn(
                      'flex items-center gap-2',
                      isContinuePlanModeForced ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs checkbox-primary"
                      checked={effectiveContinuePlanMode}
                      disabled={isContinuePlanModeForced}
                      onChange={(e) => setContinuePlanMode(e.target.checked)}
                    />
                    <span className="text-xs text-[var(--color-fg-muted)]">{tLayout('planModeLabel')}</span>
                  </label>
                </div>
              ) : (
                <span />
              )}
              <span className={`text-xs ${isContinuePromptOverLimit ? 'text-error' : 'text-[var(--color-fg-subtle)]'}`}>
                {continuePromptLength} / {PROMPT_MAX_LENGTH}
              </span>
            </div>
            <div className="sticky bottom-0 -mx-1 flex items-center justify-between gap-2 bg-base-100/95 px-1 py-2 backdrop-blur">
              <div className="flex items-center gap-1.5">
                {presetContext?.token && presetContext.projectId ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm gap-1.5"
                    onClick={() => setIsPresetModalOpen(true)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                      />
                    </svg>
                    {tLayout('promptPresetButton')}
                  </button>
                ) : null}
                <AttachmentAddButton
                  drafts={continueAttachmentDrafts}
                  onChange={setContinueAttachmentDrafts}
                  disabled={isContinuePending || isNewRequestPending}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={handleContinueCancel}
                  disabled={isContinuePending || isNewRequestPending}
                >
                  {tCommon('actions.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void handleContinueSubmit()}
                  disabled={
                    isContinuePending || isNewRequestPending || isContinuePromptOverLimit || !hasContent(continuePrompt)
                  }
                >
                  {isContinuePending || isNewRequestPending ? t('continueSending') : t('continueSendButton')}
                </button>
              </div>
            </div>
          </div>
        ) : isRetryConfirmOpen ? (
          <div className="flex flex-col gap-3">
            <div className="rounded-lg border border-error/30 bg-error/5 p-3">
              <p className="text-sm font-medium text-base-content mb-1">{t('retryConfirmTitle')}</p>
              <p className="text-xs text-[var(--color-fg-muted)]">{t('retryConfirmDescription')}</p>
            </div>
            <div className="space-y-1.5">
              <div className="grid grid-cols-2 gap-2">
                <div className="flex min-w-0 flex-col">
                  <label
                    className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                    htmlFor="daemon-detail-retry-runner-type-select"
                  >
                    {tLayout('runnerType')}
                  </label>
                  <Select
                    id="daemon-detail-retry-runner-type-select"
                    options={continueRunnerTypeOptions}
                    value={continueRunnerType}
                    onChange={handleContinueRunnerTypeChange}
                    size="sm"
                    fullWidth
                  />
                </div>
                <div className="relative flex min-w-0 flex-col">
                  <label
                    className="mb-1.5 flex h-4 items-center text-xs font-medium text-[var(--color-fg-subtle)]"
                    htmlFor="daemon-detail-retry-model-select"
                  >
                    {tLayout('runnerModel')}
                  </label>
                  {showContinueEffortSelect ? (
                    <EffortSlider
                      className="absolute right-0 top-0 -translate-y-0.5"
                      id="daemon-detail-retry-effort-select"
                      value={continueEffort}
                      onChange={setContinueEffort}
                      options={continueEffortOptions}
                      title={tLayout('runnerEffort')}
                      autoLabel={tLayout('daemonEffortAuto')}
                      compactAutoLabel={tLayout('daemonEffortDefaultShort')}
                      fasterLabel={tLayout('daemonEffortFaster')}
                      smarterLabel={tLayout('daemonEffortSmarter')}
                    />
                  ) : null}
                  <Select
                    id="daemon-detail-retry-model-select"
                    options={continueModelOptions}
                    value={continueModelOptionValue}
                    onChange={handleContinueModelChange}
                    size="sm"
                    fullWidth
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setIsRetryConfirmOpen(false)}
                disabled={isContinuePending}
              >
                {tCommon('actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-error btn-sm"
                onClick={() => void handleRetryConfirm()}
                disabled={isContinuePending}
              >
                {isContinuePending ? t('continueSending') : t('retryConfirm')}
              </button>
            </div>
          </div>
        ) : trigger.status === 'PENDING' && onDeleteAction ? (
          <div className="flex justify-end">
            <button
              type="button"
              className="btn btn-outline btn-error btn-sm"
              onClick={() => onDeleteAction(trigger)}
              disabled={isDeleting}
            >
              {isDeleting ? t('deleting') : tCommon('actions.delete')}
            </button>
          </div>
        ) : canCancel ? (
          <div className="flex justify-end">
            <button
              type="button"
              className="btn btn-outline btn-warning btn-sm"
              onClick={() => setIsCancelConfirmOpen(true)}
            >
              {tCommon('actions.cancel')}
            </button>
          </div>
        ) : (
          <div className="flex justify-end">
            {hasActiveContinueDraft ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSafeClose}>
                {tLayout('detailReturnToContinueDraft')}
              </button>
            ) : (
              <button type="button" className="btn btn-outline btn-primary btn-sm" onClick={handleContinue}>
                {trigger.status === 'FAILED' ? t('retrySend') : t('continueSend')}
              </button>
            )}
          </div>
        )}
      </>
    ) : null;

  return (
    <>
      {hasActiveContinueDraft ? (
        <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-4 py-2.5 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-xs font-medium text-primary">
              {tLayout('detailContinueDraftBannerTitle')}
            </p>
            <button
              type="button"
              onClick={handleSafeClose}
              className="shrink-0 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-content transition hover:bg-primary/90"
            >
              {tLayout('detailContinueDraftBannerAction')}
            </button>
          </div>
        </div>
      ) : null}
      {/* Header */}
      <div className="shrink-0 px-4 py-4 sm:px-6">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 text-sm">
            <Tooltip
              content={trigger.isFavorite ? t('favoriteRemove') : t('favoriteAdd')}
              mode="overflowSafe"
              placement="top"
              className="inline-flex shrink-0"
            >
              <button
                type="button"
                aria-label={trigger.isFavorite ? t('favoriteRemove') : t('favoriteAdd')}
                aria-pressed={trigger.isFavorite}
                className={detailHeaderIconButtonClassName}
                onClick={handleToggleFavorite}
                disabled={toggleFavoriteMutation.isPending}
              >
                <Star
                  className={cn(
                    detailHeaderFavoriteIconClassName,
                    trigger.isFavorite ? 'fill-current text-warning' : 'text-[var(--color-fg-muted)]',
                  )}
                  aria-hidden="true"
                />
              </button>
            </Tooltip>
            {daemonName ? (
              <span className="flex items-center gap-1 font-semibold text-base-content/80 truncate">
                <OsIcon osType={daemonOsType} />
                {daemonName}
              </span>
            ) : null}
            {daemonName && agentName ? (
              <span aria-hidden className="text-base-content/20">
                ·
              </span>
            ) : null}
            {agentName ? (
              <span className="flex items-center gap-1 font-medium text-[var(--color-fg-subtle)] truncate">
                <OsIcon osType={agentOsType} />
                {agentName}
              </span>
            ) : null}
            {!daemonName && !agentName ? (
              <span className="font-semibold text-base-content/80">{t('title')}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--color-fg-subtle)]">{formatRelativeTime(trigger.createdAt)}</span>
            <button
              type="button"
              className={detailHeaderIconButtonClassName}
              onClick={handleSafeClose}
              aria-label={tCommon('actions.close')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={detailHeaderIconClassName}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="shrink-0 px-4 sm:px-6">
        <div className="w-full border-b border-base-300" />
      </div>

      {/* Body */}
      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden p-4 sm:p-6">
        <div className="flex h-full w-full min-w-0 flex-col gap-4 sm:gap-6">
          {!isContinueInputOpen ? (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <MetaBadge tone={daemonTriggerStatusBadge[trigger.status]} tooltip={tCommon('fields.status')}>
                {daemonTriggerStatusLabel[trigger.status]}
              </MetaBadge>
              <span aria-hidden className="text-base-content/20">
                ·
              </span>
              <span className="flex items-center gap-1.5 font-medium text-[var(--color-fg-subtle)]">
                {(() => {
                  const icon = getRunnerTypeIcon(trigger.runnerType);
                  return (
                    // runner icon is selected dynamically from runtime state, so plain img is kept.
                    <img src={icon.src} alt="" className={`h-3.5 w-3.5 ${icon.mono ? 'opacity-70 dark:invert' : ''}`} />
                  );
                })()}
                {displayedRunnerModelLabel}
              </span>
              {trigger.effort ? (
                <>
                  <span aria-hidden className="text-base-content/20">
                    ·
                  </span>
                  <span className="font-medium text-[var(--color-fg-subtle)]">
                    {tLayout('runnerEffort')}: {getEffortLevelLabel(trigger.effort, tLayout)}
                  </span>
                </>
              ) : null}
              {trigger.createdByName ? (
                <>
                  <span aria-hidden className="text-base-content/20">
                    ·
                  </span>
                  <Tooltip
                    content={trigger.createdByName}
                    mode="overflowSafe"
                    showOnlyWhenTruncated
                    placement="top"
                    className="min-w-0"
                  >
                    <span className="block truncate font-medium text-[var(--color-fg-subtle)]">
                      {t('labelRequester')}: {trigger.createdByName}
                    </span>
                  </Tooltip>
                </>
              ) : null}
              <div className="ml-auto flex items-center">
                {renderAttachmentAction(trigger.id)}
                <DaemonTriggerTokenUsage key={trigger.id} usage={trigger.tokenUsage} />
                {injectedConventions.length > 0 ? (
                  <Tooltip
                    content={t('labelInjectedConventions')}
                    mode="overflowSafe"
                    placement="top"
                    className="inline-flex shrink-0"
                  >
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs gap-1.5 text-[var(--color-fg-muted)] hover:text-base-content"
                      onClick={() => setIsInjectedConventionsOpen((prev) => !prev)}
                      aria-expanded={isInjectedConventionsOpen}
                      aria-label={`${t('labelInjectedConventions')} ${injectedConventions.length}`}
                    >
                      <BookOpen className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">{injectedConventions.length}</span>
                    </button>
                  </Tooltip>
                ) : null}
              </div>
            </div>
          ) : null}

          {!isContinueInputOpen && injectedConventions.length > 0 && isInjectedConventionsOpen ? (
            <div className="shrink-0 -mt-2 sm:-mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
                {t('injectedConventionsTitle')}
              </p>
              <ul className="space-y-1.5">
                {injectedConventions.map((item) => (
                  <li key={item.id} className="flex min-w-0 items-center gap-2 text-xs">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        item.source === 'AUTO_MATCH'
                          ? 'bg-info/10 text-info'
                          : item.source === 'USER_CUSTOM'
                            ? 'bg-secondary/10 text-secondary'
                            : 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]'
                      }`}
                    >
                      {t(`injectedConventionSource_${item.source}` as Parameters<typeof t>[0])}
                    </span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-medium',
                        conventionCategoryBadge[item.convention.category as keyof typeof conventionCategoryBadge] ??
                          'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
                      )}
                    >
                      {item.convention.category}
                    </span>
                    <span
                      className={`truncate ${item.convention.deletedAt ? 'line-through text-[var(--color-fg-muted)]' : 'text-base-content/80'}`}
                    >
                      {item.convention.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {trigger.useWorktree ? (
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <p className="shrink-0 text-xs font-semibold tracking-wider text-[var(--color-fg-subtle)]">
                  {t('worktreeLabel')}
                </p>
                {runnerBoxInfo?.kind === 'managed' && runnerBoxInfo.worktreeId ? (
                  <WorktreeAliasEditor
                    worktreeId={runnerBoxInfo.worktreeId}
                    alias={runnerBoxInfo.alias}
                    onSave={handleWorktreeAliasSave}
                    editLabel={tWorktreeAlias('edit')}
                    placeholder={tWorktreeAlias('placeholder')}
                    saveLabel={tCommon('actions.save')}
                    cancelLabel={tCommon('actions.cancel')}
                    isSaving={worktreeAliasMutation.isPending}
                    readOnly={(runnerBoxInfo.status ?? 'ACTIVE') !== 'ACTIVE'}
                    variant="detail"
                  />
                ) : null}
                {runnerBoxInfo?.kind === 'discovered' ? (
                  <>
                    <span className="min-w-0 truncate text-xs font-medium text-[var(--color-fg-subtle)]">
                      {runnerBoxInfo.name ?? t('worktreeDiscoveredFallbackName')}
                    </span>
                    {runnerBoxInfo.branch ? (
                      <span className="min-w-0 truncate font-mono text-xs text-[var(--color-fg-subtle)]">
                        {t('worktreeBranch', { branch: runnerBoxInfo.branch })}
                      </span>
                    ) : null}
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-1 text-xs font-medium',
                        runnerBoxInfo.status === 'AVAILABLE' &&
                          'bg-[var(--chip-success-bg)] text-[var(--chip-success-fg)]',
                        runnerBoxInfo.status === 'MISSING' &&
                          'bg-[var(--chip-warning-bg)] text-[var(--chip-warning-fg)]',
                        runnerBoxInfo.status === null && 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
                      )}
                    >
                      {t(`worktreeDiscoveredStatus${runnerBoxInfo.status ?? 'UNKNOWN'}` as Parameters<typeof t>[0])}
                    </span>
                  </>
                ) : (
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-1 text-xs font-medium',
                      worktreeStatusBadge[runnerBoxInfo?.status ?? trigger.worktreeStatus ?? 'ACTIVE'] ??
                        'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
                    )}
                  >
                    {t(
                      `worktreeStatus${runnerBoxInfo?.status ?? trigger.worktreeStatus ?? 'ACTIVE'}` as Parameters<
                        typeof t
                      >[0],
                    )}
                  </span>
                )}
              </div>
              {onWorktreeManageAction && runnerBoxInfo?.kind !== 'discovered' ? (
                <button type="button" className="btn btn-outline btn-xs" onClick={onWorktreeManageAction}>
                  {t('worktreeManage')}
                </button>
              ) : null}
            </div>
          ) : null}

          {!isContinueInputOpen ? (
            <div className="shrink-0">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold text-[var(--color-fg-subtle)]">{t('labelPrompt')}</p>
                <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
                  {isSystemAdmin ? (
                    <Tooltip
                      content={tLayout('copyRunnerPromptHint')}
                      mode="overflowSafe"
                      placement="top"
                      className="inline-flex shrink-0"
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--color-fg-muted)] hover:text-base-content disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={handleCopyRunnerPrompt}
                        disabled={isCopyingRunnerPrompt}
                        aria-label={tLayout('copyRunnerPromptHint')}
                      >
                        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                        <span>
                          {isCopyingRunnerPrompt ? tLayout('copyingRunnerPrompt') : tLayout('copyRunnerPrompt')}
                        </span>
                      </button>
                    </Tooltip>
                  ) : null}
                  {presetContext?.token && presetContext.projectId
                    ? renderSavePresetButton({
                        token: presetContext.token,
                        projectId: presetContext.projectId,
                        prompt: displayPrompt as object,
                        userConventionIds: trigger.userConventionIds ?? [],
                        userConventionOptions: userConventionOptions ?? [],
                        canWrite: presetContext.canWrite ?? true,
                      })
                    : null}
                </div>
              </div>
              <div className="[&_.tiptap_p]:!mb-1">
                <CollapsibleContent
                  contentKey={trigger.id}
                  threshold={72}
                  mobileThreshold={64}
                  expandLabel={t('promptShowAll')}
                  buttonAlign="end"
                  fadeSize="sm"
                  controlDensity="compact"
                >
                  {renderPromptViewer(JSON.stringify(displayPrompt))}
                </CollapsibleContent>
              </div>
            </div>
          ) : null}

          <div className="-mt-1 flex min-h-0 flex-1 basis-0 flex-col">
            <div className="mb-3 flex min-w-0 items-end justify-between gap-3 border-b border-base-300">
              {sectionTabs}
              {sectionActions}
            </div>
            <div ref={logsContainerRef} onScroll={handleLogsScroll} className="min-h-0 flex-1 space-y-2 overflow-auto">
              {logsContent}
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isLogsFullscreenOpen ? (
        <div className="absolute inset-0 z-20 flex flex-col bg-base-100">
          <div className="px-6 py-4 shrink-0">
            <div className="flex w-full items-center justify-between">
              <p className="text-base font-semibold">
                {isErrorSelected ? 'Error' : isCoActionSelected ? 'CoAction' : t('labelLogs')}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-fg-subtle)]">
                  {formatDaemonTriggerDate(trigger.createdAt)}
                </span>
                {showCoActionLink && showCoActionSection && isCoActionSelected && trigger.linkedCoAction ? (
                  <a
                    href={`/co-actions/detail?id=${trigger.linkedCoAction.id}${contextQuery.replace('?', '&')}`}
                    className="btn btn-ghost btn-sm btn-circle text-[var(--color-fg-muted)] hover:text-base-content"
                    aria-label={t('openSessionSummary')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </a>
                ) : null}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-circle text-[var(--color-fg-muted)] hover:text-base-content"
                  onClick={() => setIsLogsFullscreenOpen(false)}
                  aria-label="Close fullscreen"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className="shrink-0 px-6">
            <div className="w-full border-b border-base-300" />
          </div>
          <div
            ref={logsFullscreenRef}
            onScroll={handleFullscreenLogsScroll}
            className="min-h-0 flex-1 overflow-auto px-6 py-3"
          >
            <div className="w-full space-y-2">{logsContent}</div>
          </div>
        </div>
      ) : null}

      {/* Footer */}
      {footerContent ? (
        <>
          <div className="shrink-0 px-4 sm:px-6">
            <div className="w-full border-t border-base-300" />
          </div>
          <div className="min-h-0 shrink overflow-y-auto overscroll-contain bg-base-100/95 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] backdrop-blur sm:p-6 sm:pb-6">
            <div className="w-full">{footerContent}</div>
          </div>
        </>
      ) : null}

      {/* Preset modal */}
      {presetContext?.token && presetContext.projectId ? (
        <PromptPresetModal
          isOpen={isPresetModalOpen}
          onClose={() => setIsPresetModalOpen(false)}
          token={presetContext.token}
          projectId={presetContext.projectId}
          memberId={presetContext.memberId ?? null}
          canWrite={presetContext.canWrite}
          onSelectPreset={(prompt, userConventionIds) => {
            setContinuePrompt((current) => appendPromptContent(current, prompt));
            setContinueUserConventionIds(userConventionIds);
          }}
          currentPrompt={continuePrompt}
          currentUserConventionIds={continueUserConventionIds}
          userConventionOptions={userConventionOptions ?? []}
        />
      ) : null}
    </>
  );
}
